import { useEffect, useRef } from 'react';

/**
 * Global keyboard shortcut registry. `ShortcutsDialog` and the `/help/shortcuts` page render this list, so add
 * new global shortcuts here. Key syntax: single keys (`n`, `/`, `?`, `Escape`), `mod+k` (⌘ on macOS, Ctrl
 * elsewhere), and two-key sequences separated by a space (`g h`).
 */
export interface ShortcutDef {
  readonly id: ShortcutId;
  readonly keys: string;
  readonly description: string;
  readonly group: 'General' | 'Navigation' | 'Actions' | 'Register';
}
export type ShortcutId =
  | 'help' | 'palette' | 'search' | 'close' | 'newTransaction'
  | 'goHome' | 'goAccounts' | 'goTransactions' | 'goBudget' | 'goGoals' | 'goInvest' | 'goReports'
  | 'regNext' | 'regPrev' | 'regInline' | 'regEdit' | 'regSelect' | 'regCategorise' | 'regReviewed' | 'regDelete'
  | 'regDuplicate' | 'regSaveNew' | 'regQuickAdd';

export const SHORTCUTS: readonly ShortcutDef[] = [
  { id: 'palette', keys: 'mod+k', description: 'Open the command palette', group: 'General' },
  { id: 'search', keys: '/', description: 'Search', group: 'General' },
  { id: 'help', keys: '?', description: 'Help for this page', group: 'General' },
  { id: 'close', keys: 'Escape', description: 'Close dialog or panel', group: 'General' },
  { id: 'newTransaction', keys: 'n', description: 'New transaction', group: 'Actions' },
  { id: 'goHome', keys: 'g h', description: 'Go to Home', group: 'Navigation' },
  { id: 'goAccounts', keys: 'g a', description: 'Go to Accounts', group: 'Navigation' },
  { id: 'goTransactions', keys: 'g t', description: 'Go to Transactions', group: 'Navigation' },
  { id: 'goBudget', keys: 'g b', description: 'Go to Budget', group: 'Navigation' },
  { id: 'goGoals', keys: 'g g', description: 'Go to Goals', group: 'Navigation' },
  { id: 'goInvest', keys: 'g i', description: 'Go to Invest', group: 'Navigation' },
  { id: 'goReports', keys: 'g r', description: 'Go to Reports', group: 'Navigation' },
  // Transactions register (components/money/Register.tsx handles these; `n` opens its editor when one is on the page)
  { id: 'regNext', keys: 'j', description: 'Next transaction', group: 'Register' },
  { id: 'regPrev', keys: 'k', description: 'Previous transaction', group: 'Register' },
  { id: 'regInline', keys: 'Enter', description: 'Edit inline', group: 'Register' },
  { id: 'regEdit', keys: 'e', description: 'Open the full editor', group: 'Register' },
  { id: 'regSelect', keys: 'x', description: 'Select / deselect', group: 'Register' },
  { id: 'regCategorise', keys: 'c', description: 'Categorise', group: 'Register' },
  { id: 'regReviewed', keys: 'r', description: 'Mark reviewed', group: 'Register' },
  { id: 'regDelete', keys: 'd', description: 'Delete (with undo)', group: 'Register' },
  { id: 'regDuplicate', keys: 'mod+d', description: 'Duplicate', group: 'Register' },
  { id: 'regSaveNew', keys: 'mod+Enter', description: 'Save and add another', group: 'Register' },
  { id: 'regQuickAdd', keys: 'q', description: 'Focus quick add', group: 'Register' },
];

export const isMac = (): boolean => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/** Display tokens for a key spec: `mod+k` → ['⌘', 'K'] on macOS. */
export const keyLabels = (keys: string): string[][] =>
  keys.split(' ').map((step) =>
    step.split('+').map((k) => {
      if (k === 'mod') return isMac() ? '⌘' : 'Ctrl';
      if (k === 'Escape') return 'Esc';
      if (k === 'Enter') return 'Enter';
      if (k === 'ArrowRight') return '→';
      return k.length === 1 ? k.toUpperCase() : k;
    }),
  );

const isTypingTarget = (t: EventTarget | null): boolean => {
  if (!(t instanceof HTMLElement)) return false;
  if (t.isContentEditable) return true;
  if (t instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'range'].includes(t.type);
  return t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement || t.getAttribute('role') === 'combobox';
};

/** Does a keyboard event match one step like `mod+k`, `?`, `Escape`, `c`? */
export const matchesStep = (e: KeyboardEvent, step: string): boolean => {
  const parts = step.split('+');
  const key = parts[parts.length - 1] ?? '';
  const wantMod = parts.includes('mod');
  const hasMod = e.metaKey || e.ctrlKey;
  if (wantMod !== hasMod) return false;
  if (!wantMod && e.altKey) return false;
  return e.key.toLowerCase() === key.toLowerCase();
};

export interface HotkeyOptions {
  /** Fire even when focus is in a text field (default: only `Escape` and `mod+` combos do). */
  readonly allowInInputs?: boolean;
  readonly enabled?: boolean;
}

/**
 * Bind keys → handlers for the lifetime of the component. Sequences (`g h`) must complete within 1 s.
 * The default is prevented when a binding fires; return `false` from a handler to decline the key
 * (e.g. let Enter activate a focused button).
 */
export const useHotkeys = (bindings: Record<string, (e: KeyboardEvent) => unknown>, opts: HotkeyOptions = {}): void => {
  const ref = useRef(bindings);
  ref.current = bindings;
  const pending = useRef<{ key: string; at: number } | null>(null);
  const { allowInInputs = false, enabled = true } = opts;

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const typing = isTypingTarget(e.target);
      const now = Date.now();
      const prefix = pending.current && now - pending.current.at < 1000 ? pending.current.key : null;
      for (const [spec, handler] of Object.entries(ref.current)) {
        const steps = spec.split(' ');
        const last = steps[steps.length - 1] ?? '';
        const isGlobalCombo = last.startsWith('mod+') || last === 'Escape';
        if (typing && !allowInInputs && !isGlobalCombo) continue;
        if (steps.length === 2) {
          if (prefix === steps[0] && matchesStep(e, last)) {
            pending.current = null;
            if (handler(e) !== false) e.preventDefault();
            return;
          }
        } else if (matchesStep(e, last)) {
          pending.current = null;
          if (handler(e) !== false) {
            e.preventDefault();
            return;
          }
        }
      }
      const startsSequence = !typing && Object.keys(ref.current).some((s) => s.split(' ').length === 2 && s.startsWith(`${e.key} `));
      pending.current = startsSequence ? { key: e.key, at: now } : null;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [allowInInputs, enabled]);
};
